import React, { Suspense, useEffect, useState, useRef, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useLocalStorage } from '../../../hooks/useLocalStorage';
import ChildAnalysisTab from './Tabs/ChildAnalysisTab';
import ComparisonAnalysisTab from './Tabs/ComparisonAnalysisTab';
import DurationAnalysisTab from './Tabs/DurationAnalysisTab';
import EnergyAnalysisTab from './Tabs/EnergyAnalysisTab';
import EnergyOverviewTab from './Tabs/EnergyOverviewTab';
import EnergyTreeGraphTab from './Tabs/EnergyTreeGraphTab';
import EsgOverviewTab from './Tabs/EsgOverviewTab';
import HourAnalysisTab from './Tabs/HourAnalysisTab';
import InitiativesTab from './Tabs/InitiativesTab';
import StandardAnalysisTab from './Tabs/StandardAnalysisTab';
import { useNavigate } from 'react-router-dom';

import { getTimestampSummaryValidationStatus } from '@/utils/getTimestampSummaryValidationStatus';

import {
  Autocomplete,
  Box,
  CircularProgress,
  Container,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

import { getSelectedAccount } from '../../../redux/selectedAccountSlice';

import { useTranslation } from 'react-i18next';

import { useLazyGetLocationQuery } from '../../../api/dataforsyningen/dataforsyningenApiSlice';
import { useGetTimestampSummaryQuery } from '@/api/energy-analysis/energyDataApiSlice';
import { useGetUnitsByAccountQuery } from '../../../api/recalc/endpoints/unitsApiSlice';
import { showSnackbar } from '../../../redux/snackbarSlice';

import {
  useGetLocationByIdQuery,
  usePostLocationMutation,
} from '../../../api/recalcV2/endpoints/locationsApiSlice';

import {
  useDeleteEnergyGroupMutation,
  useGetEnergyGroupsQuery,
  usePostEnergyGroupMutation,
  usePutEnergyGroupMutation,
  usePutEnergyGroupOptionsMutation,
} from '../../../api/recalcV2/endpoints/energyGroupsApiSlice';

import { useGetAlarmsFeedByAccountQuery } from '../../../api/recalc/endpoints/alarmsApiSlice';

import { DateTime } from 'luxon';

import PowerLoadProfilesAnalysisTab from './Tabs/PowerLoadProfilesAnalysisTab';
import PrintDialog from '@/layout/pages/EnergyXplorer/Print/PrintDialog';
import { tabToKey } from '@/layout/pages/EnergyXplorer/printFlags';
import { energyAnalysisApiSlice } from '@/api/apiSlice';
import NoEnergyGroupFoundCard from '@/features/energyxplorer/components/card/NoEnergyGroupFoundCard';
import CreateEnergyGroupDialog from '@/features/energyxplorer/components/CreateEnergyGroupDialog';
import EditEnergyGroupDialog from '@/features/energyxplorer/components/EditEnergyGroupDialog';
import DeleteEnergyGroupDialog from '@/features/energyxplorer/components/DeleteEnergyGroupDialog';
import AppBarMenu from '@/features/energyxplorer/components/AppBarMenu';
import {
  selectTimeRange,
  setTimeRange,
} from '@/features/energyxplorer/redux/timeRangeSlice';

import DateRangeSelector from '@/components/time-range-selector/DateRangeSelector';
import { en } from '@fullcalendar/core/internal-common';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Box sx={{ py: 2 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const EnergyXplorerPage = () => {
  const [energyGroupId, setEnergyGroupId] = useLocalStorage(
    'energyGroupId',
    null,
  );

  const { t } = useTranslation('energyxplorer');
  const hasManuallyAdjustedOnce = useRef(false);

  //===Redux===//
  const selectedAccount = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //===State===//
  const [selectedEnergyGroup, setSelectedEnergyGroup] = useState(null);
  const [value, setValue] = useState(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [nodeToAnalyse, setNodeToAnalyse] = useState(null);
  const [analysisMode, setAnalysisMode] = useState(null); // or 'seu'

  const [seuToAnalyze, setSeuToAnalyze] = useState(null);
  const [openCreateEnergyGroupDialog, setOpenCreateEnergyGroupDialog] =
    useState(false);
  const [deleteEnergyGroupDialogOpen, setDeleteEnergyGroupDialogOpen] =
    useState(false);
  const [openEditEnergyGroupDialog, setOpenEditEnergyGroupDialog] =
    useState(false);

  //===API v1===//
  const { data: units } = useGetUnitsByAccountQuery(
    selectedAccount?.AccountId,
    {
      skip: !selectedAccount?.AccountId,
    },
  );

  const [triggerGetLocation] = useLazyGetLocationQuery();

  const { data: alarmsFeed } = useGetAlarmsFeedByAccountQuery(
    selectedAccount?.AccountId,
    {
      skip: !selectedAccount?.AccountId,
    },
  );

  //===API v2===//
  const [postLocation] = usePostLocationMutation();
  const [postEnergyGroup] = usePostEnergyGroupMutation();
  const [putEnergyGroup] = usePutEnergyGroupMutation();
  const [deleteEnergyGroup] = useDeleteEnergyGroupMutation();
  const [putEnergyGroupOptions] = usePutEnergyGroupOptionsMutation();

  const { data: locationData } = useGetLocationByIdQuery(
    {
      locationId: selectedEnergyGroup?.locationId,
      accountId: selectedAccount?.AccountId,
    },
    { skip: !selectedEnergyGroup?.locationId },
  );

  const { data: allEnergyGroups, isLoading: isLoadingAllEnergyGroups } =
    useGetEnergyGroupsQuery(selectedAccount?.AccountId, {
      skip: !selectedAccount?.AccountId,
      pollingInterval: 60000,
      skipPollingIfUnfocused: true,
    });

  const prevIsNotSynced = useRef(false);

  const isNotSynced = useMemo(() => {
    if (!selectedEnergyGroup) return false;
    return selectedEnergyGroup.options.state !== 'Synced';
  }, [selectedEnergyGroup]);

  useEffect(() => {
    if (prevIsNotSynced.current === true && isNotSynced === false) {
      dispatch(
        energyAnalysisApiSlice.util.invalidateTags([
          'EnergyAnalysis',
          'EnergyData',
        ]),
      );
    }
    prevIsNotSynced.current = isNotSynced;
  }, [isNotSynced, dispatch]);

  //===Time Range===//
  const { data: validTimestampSummary } = useGetTimestampSummaryQuery(
    {
      accountId: selectedAccount?.AccountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
    },
    {
      skip: !selectedEnergyGroup?.energyGroupId,
    },
  );

  const timeRange = useSelector(selectTimeRange);

  const setTimeRangeHandler = (timeRange) => {
    dispatch(setTimeRange(timeRange));
  };

  const [timestampSummarySetBy, setTimestampSummarySetBy] = useState(null);
  const [timestampSummary, setTimestampSummary] = useState(null);
  const [duration, setDuration] = useState(14); // Default duration of 14 days

  const validation = useMemo(() => {
    return getTimestampSummaryValidationStatus(
      timeRange,
      validTimestampSummary,
    );
  }, [timeRange, validTimestampSummary]);

  useEffect(() => {
    if (hasManuallyAdjustedOnce.current) return; // ✅ skip after first adjustment

    if (validation.isInverted) {
      hasManuallyAdjustedOnce.current = true;
      return;
    }

    if (validation.isCompletelyInside) {
      hasManuallyAdjustedOnce.current = true;
      return;
    }
    if (validation.isCompletelyOutside) {
      dispatch(
        setTimeRange({
          start: validation.maxEarliest.toMillis(),
          end: validation.minLatest.toMillis(),
        }),
      );
      hasManuallyAdjustedOnce.current = true;
      return;
    }

    if (validation.isOutOfBounds && !validation.isCompletelyOutside) {
      dispatch(
        setTimeRange({
          start: validation.adjustedRange[1]
            .minus({ weeks: 4 })
            .startOf('day')
            .toMillis(),
          end: validation.adjustedRange[1].toMillis(),
        }),
      );
      dispatch(
        showSnackbar({
          message:
            'Slutningen af tidsintervallet er justeret til senest gyldige datagrundlag.',
          severity: 'info',
        }),
      );
      hasManuallyAdjustedOnce.current = true;
    }
  }, [dispatch, validation]);

  const [esgTimestampSummary, setEsgTimestampSummary] = useState(null);
  const [esgDuration, setEsgDuration] = useState(14); // Default duration of 14 days

  const [esgTimeRange, setEsgTimeRange] = useState({
    start: DateTime.now()
      .startOf('year')
      .minus({ years: 1 })
      .toUTC()
      .toMillis(),
    end: DateTime.now().endOf('day').minus({ days: 1 }).toUTC().toMillis(),
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSaveChanges = async (updatedGroup) => {
    if (!selectedEnergyGroup?.energyGroupId) return;

    const payload = {
      energyGroupId: selectedEnergyGroup.energyGroupId,
      accountId: selectedAccount.AccountId,
      name: updatedGroup.name,
      locationId: updatedGroup.locationId,
      options: updatedGroup.options || {},
      type: updatedGroup.type || 'power',
    };

    try {
      await putEnergyGroup(payload).unwrap();
      dispatch(
        showSnackbar({ message: 'Gruppe opdateret', severity: 'success' }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i opdatering af gruppe',
          severity: 'error',
        }),
      );
    }

    try {
      await putEnergyGroupOptions({
        accountId: selectedAccount.AccountId,
        energyGroupId: selectedEnergyGroup.energyGroupId,
        options: updatedGroup.options || {},
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Gruppe indstillinger opdateret',
          severity: 'success',
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i opdatering af gruppe indstillinger',
          severity: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    if (selectedAccount && allEnergyGroups && energyGroupId) {
      const parsedId = parseInt(energyGroupId, 10);
      const selectedEnergyGroupFromStorage = allEnergyGroups.find(
        (group) => group.energyGroupId === parsedId,
      );

      if (selectedEnergyGroupFromStorage) {
        setSelectedEnergyGroup(selectedEnergyGroupFromStorage);
      } else {
        setSelectedEnergyGroup(allEnergyGroups[0] || null);
      }
    }
  }, [allEnergyGroups, selectedAccount, energyGroupId]);

  useEffect(() => {
    // setValue(0);
    hasManuallyAdjustedOnce.current = false;
  }, [selectedEnergyGroup?.energyGroupId]);

  // * MOVE TO DELETE DIALOG
  const handleDeleteEnergyGroup = async (energyGroupId) => {
    try {
      await deleteEnergyGroup({
        energyGroupId: energyGroupId,
        accountId: selectedAccount.AccountId,
      }).unwrap();
      dispatch(
        showSnackbar({ message: 'Gruppe slettet', severity: 'success' }),
      );
      setDeleteEnergyGroupDialogOpen(false);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i sletning af gruppe',
          severity: 'error',
        }),
      );
    }
  };

  // const handleAlarmClick = (alarm) => {
  //   navigate(`/alarms/${alarm.AlarmId}`, {
  //     state: { alarm: alarm },
  //   });
  // };

  const handleAlarmClick = React.useCallback(
    (alarm) => {
      navigate(`/alarms/${alarm.AlarmId}`, {
        state: { alarm: alarm },
      });
    },
    [navigate],
  );

  // * MOVE TO CREATE DIALOG
  const handleAddGroup = async (newGroup) => {
    if (!newGroup.location) return;

    try {
      const locationResponse = await postLocation({
        accountId: selectedAccount.AccountId,
        location: newGroup.location,
      }).unwrap();

      const energyGroupData = {
        name: newGroup.name,
        accountId: selectedAccount.AccountId,
        locationId: locationResponse.locationId,
        options: newGroup.options || {}, // Include options if provided
        type: newGroup.type || 'power', // Default to 'power' if not provided
      };

      try {
        await postEnergyGroup({
          energyGroup: energyGroupData,
          accountId: selectedAccount.AccountId,
        }).unwrap();
        dispatch(
          showSnackbar({ message: 'Gruppe oprettet', severity: 'success' }),
        );
      } catch (error) {
        if (error?.status === 500 && error?.data?.energyGroupId) {
          dispatch(
            showSnackbar({
              message:
                'Gruppe oprettet, men der opstod en fejl (500). Fortsætter med indstillinger...',
              severity: 'warning',
            }),
          );
        } else {
          dispatch(
            showSnackbar({
              message: 'Der opstod en fejl i oprettelse af gruppe',
              severity: 'error',
            }),
          );
        }
      }
    } catch (error) {
      console.error('Error posting location:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i oprettelse af lokation',
          severity: 'error',
        }),
      );
    }
  };

  const handleSEURowClick = (seu) => {
    setSeuToAnalyze(seu);
    setAnalysisMode('seu');
    if (value === 0) {
      setValue(2);
    }
  };

  const handleNodeClick = (node) => {
    setNodeToAnalyse(node);
    setAnalysisMode('node');
    if (value === 0) {
      setValue(2);
    }
  };

  const handleEnergyGroupChange = (event, value) => {
    setSelectedEnergyGroup(value);
    setEnergyGroupId(value?.energyGroupId || null); // Update local storage with the selected energy group ID
  };

  const handleEnergyGroupCreate = () => {
    setOpenCreateEnergyGroupDialog(true);
  };

  const handleEnergyGroupSettingsClick = () => {
    setOpenEditEnergyGroupDialog(true);
  };

  // useEffect(() => {
  //   if (!selectedEnergyGroup) return;
  //   if (!selectedEnergyGroup?.energyTreeNodes?.length > 0) {
  //     setValue(1); // Switch to the Energy Tree Graph tab when an energy group is selected
  //   }
  // }, [selectedEnergyGroup, value]);

  //Print feature

  const [printOpen, setPrintOpen] = useState({ open: false, page: 'all' });
  const handlePrintClick = (page) => {
    setPrintOpen({ open: true, page: tabToKey(page) });
  };

  return (
    <>
      <Container maxWidth="xxl">
        <Suspense fallback={<div>Loading EnergyGroupSettings...</div>}>
          <AppBarMenu
            setValue={setValue}
            alarms={alarmsFeed}
            value={value}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            selectedEnergyGroup={selectedEnergyGroup}
            handleClose={handleClose}
            handleClick={handleClick}
            handleEnergyGroupCreate={handleEnergyGroupCreate}
            handleEnergyGroupSettingsClick={handleEnergyGroupSettingsClick}
            setDeleteEnergyGroupDialogOpen={setDeleteEnergyGroupDialogOpen}
            handlePrintClick={handlePrintClick}
            handleAlarmClick={handleAlarmClick}
            dateRangeComponent={
              value === 4 ? (
                // Esg daterangeselector
                <DateRangeSelector
                  setTimeRange={setEsgTimeRange}
                  timeRange={esgTimeRange}
                  setDuration={setEsgDuration}
                  timestampSummary={esgTimestampSummary}
                  enableEsgShortcuts
                />
              ) : (
                // Normal datarangeselector
                <DateRangeSelector
                  setTimeRange={setTimeRangeHandler}
                  timeRange={timeRange}
                  setDuration={setDuration}
                  timestampSummary={timestampSummary}
                  timestampSummarySetBy={timestampSummarySetBy}
                />
              )
            }
            energyGroupSelector={
              <Autocomplete
                options={allEnergyGroups || []}
                getOptionLabel={(option) => option.name}
                value={selectedEnergyGroup || null}
                noOptionsText={t('energy_group_select.no_energy_groups')} // Dynamic translation
                disableClearable
                sx={{ minWidth: '250px' }}
                popupIcon={<UnfoldMoreRoundedIcon />}
                onChange={handleEnergyGroupChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t('energy_group_select.select_energy_group')}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: '10px',
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderRadius: '10px',
                      },
                      '& .MuiFilledInput-root': {
                        borderRadius: '5px',
                      },
                      '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after':
                        {
                          display: 'none',
                        },
                      '& .MuiInputLabel-root': {
                        color: 'gray',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                      },
                      '& .MuiOutlinedInput-root': {
                        color: 'gray',
                        borderRadius: '10px',
                        padding: 0,
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'divider',
                        },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      endAdornment: (
                        <>
                          {isLoadingAllEnergyGroups && (
                            <InputAdornment position="end">
                              <CircularProgress size={18} />
                            </InputAdornment>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            }
          />
        </Suspense>
      </Container>
      <Suspense>
        <Container maxWidth="xxl">
          {!allEnergyGroups?.length > 0 ? (
            <Suspense fallback={<div>Loading...</div>}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 8,
                }}
              >
                <NoEnergyGroupFoundCard
                  handleEnergyGroupCreate={handleEnergyGroupCreate}
                />
              </Box>
            </Suspense>
          ) : null}

          <TabPanel value={value} index={0}>
            {selectedEnergyGroup && (
              <EnergyOverviewTab
                selectedEnergyGroup={selectedEnergyGroup}
                selectedAccount={selectedAccount}
                locationData={locationData}
                timeRange={timeRange}
                handleSEURowClick={handleSEURowClick}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={1}>
            {selectedEnergyGroup && (
              <EnergyTreeGraphTab
                selectedEnergyGroup={selectedEnergyGroup}
                units={units}
                timeRange={timeRange}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
                setNodeToAnalyse={handleNodeClick}
                nodeToAnalyse={nodeToAnalyse}
                setValue={setValue}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={2}>
            {selectedEnergyGroup && (
              <EnergyAnalysisTab
                selectedAccount={selectedAccount}
                focusedNode={nodeToAnalyse}
                focusedSeu={seuToAnalyze}
                initialMode={analysisMode}
                selectedEnergyGroup={selectedEnergyGroup}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
                duration={duration}
                timeRange={timeRange}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={21}>
            {selectedEnergyGroup && (
              <ComparisonAnalysisTab
                selectedAccount={selectedAccount}
                selectedEnergyGroup={selectedEnergyGroup}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
                timeRange={timeRange}
                duration={duration}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={23}>
            {selectedEnergyGroup && (
              <DurationAnalysisTab
                selectedAccount={selectedAccount}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
                selectedEnergyGroup={selectedEnergyGroup}
                timeRange={timeRange}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={24}>
            {selectedEnergyGroup && (
              <HourAnalysisTab
                selectedAccount={selectedAccount}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
                selectedEnergyGroup={selectedEnergyGroup}
                timeRange={timeRange}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={25}>
            {selectedEnergyGroup && (
              <ChildAnalysisTab
                selectedAccount={selectedAccount}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
                selectedEnergyGroup={selectedEnergyGroup}
                timeRange={timeRange}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={26}>
            {selectedEnergyGroup && (
              <StandardAnalysisTab
                selectedEnergyGroup={selectedEnergyGroup}
                selectedAccount={selectedAccount}
                timeRange={timeRange}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={27}>
            {selectedEnergyGroup && (
              <PowerLoadProfilesAnalysisTab
                selectedEnergyGroup={selectedEnergyGroup}
                selectedAccount={selectedAccount}
                timeRange={timeRange}
                setTimestampSummary={setTimestampSummary}
                setTimestampSummarySetBy={setTimestampSummarySetBy}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={3}>
            {selectedEnergyGroup && (
              <InitiativesTab selectedEnergyGroup={selectedEnergyGroup} />
            )}
          </TabPanel>

          <TabPanel value={value} index={4}>
            {selectedEnergyGroup && (
              <EsgOverviewTab
                selectedEnergyGroup={selectedEnergyGroup}
                selectedAccount={selectedAccount}
                timeRange={esgTimeRange}
                setTimeRange={setEsgTimeRange}
              />
            )}
          </TabPanel>

          <Suspense fallback={<div></div>}>
            <CreateEnergyGroupDialog
              open={openCreateEnergyGroupDialog}
              onClose={() => setOpenCreateEnergyGroupDialog(false)}
              onCreate={handleAddGroup}
              triggerGetLocation={triggerGetLocation}
              selectedAccount={selectedAccount}
            />
          </Suspense>

          <Suspense fallback={<div></div>}>
            <EditEnergyGroupDialog
              open={openEditEnergyGroupDialog}
              onClose={() => setOpenEditEnergyGroupDialog(false)}
              triggerGetLocation={triggerGetLocation}
              selectedEnergyGroup={selectedEnergyGroup}
              locationData={locationData}
              onUpdate={handleSaveChanges}
            />
          </Suspense>

          <Suspense fallback={<div></div>}>
            <DeleteEnergyGroupDialog
              open={deleteEnergyGroupDialogOpen}
              onClose={() => setDeleteEnergyGroupDialogOpen(false)}
              selectedEnergyGroup={selectedEnergyGroup}
              handleDeleteEnergyGroup={handleDeleteEnergyGroup}
              handleClose={handleClose}
            />
          </Suspense>
          <Suspense fallback={<CircularProgress />}>
            <PrintDialog
              open={printOpen.open}
              onClose={() => setPrintOpen(false)}
              selectedAccount={selectedAccount}
              setTimestampSummary={setTimestampSummary}
              setTimestampSummarySetBy={setTimestampSummarySetBy}
              selectedEnergyGroup={selectedEnergyGroup}
              timeRange={timeRange}
              setTimeRange={setTimeRangeHandler}
              setDuration={setDuration}
              timestampSummary={timestampSummary}
              timestampSummarySetBy={timestampSummarySetBy}
              units={units}
              focusedNode={nodeToAnalyse}
              focusedSeu={seuToAnalyze}
              initialMode={analysisMode}
              duration={duration}
              pageToPrint={printOpen.page}
            />
          </Suspense>
        </Container>
      </Suspense>
    </>
  );
};

export default EnergyXplorerPage;
